import {
  IApp,
  LinuxArtifactName,
  MacArtifactName,
  WindowsArtifactName,
  Arch,
  MacArch,
  Build,
} from '@todesktop/shared';
import { encryptToBase64 } from './crypto';
import {
  AppleFilled,
  WindowsFilled,
  QqOutlined,
  DesktopOutlined,
} from '@ant-design/icons';

const getStage = (): string => {
  if (process.env.NODE_ENV === 'development') return 'dev';
  if (process.env.NODE_ENV === 'production') return 'prod';
  return process.env.NODE_ENV || 'dev';
};

const stage = getStage();

const awsLambdaBase = `https://sls-${stage}.todesktop.com`;

interface IDownloadMeta {
  wasArtifactCreated: boolean;
  artifactName?: LinuxArtifactName | MacArtifactName | WindowsArtifactName;
  arch?: Arch | MacArch;
  platform: 'mac' | 'windows' | 'linux' | 'universal';
  releasedBuild?: Build | null;
  app: IApp;
  label: string;
}

/**
 * The custom domain an app's links use: none while it's still being set
 * up (IApp customDomainStatus), so links never point at a domain that
 * doesn't serve yet.
 */
export const liveCustomDomain = (app: {
  customDomain?: string | null;
  customDomainStatus?: IApp['customDomainStatus'];
}): string | undefined =>
  app.customDomain && app.customDomainStatus !== 'pending'
    ? app.customDomain
    : undefined;

export const getDownloadMeta = (downloadsInfo: IDownloadMeta) => {
  const shouldShow = shouldShowDownloadUrl(downloadsInfo);
  const { app, arch, artifactName, platform, label } = downloadsInfo;
  const downloadUrl = getDownloadUrl({
    appId: app.id,
    platform,
    arch,
    artifactName,
    domain: liveCustomDomain(app),
  });

  let icon: any;
  if (platform === 'mac') {
    icon = AppleFilled;
  } else if (platform === 'windows') {
    icon = WindowsFilled;
  } else if (platform === 'linux') {
    icon = QqOutlined;
  } else {
    icon = DesktopOutlined;
  }

  return { shouldShow, downloadUrl, icon, label };
};

export interface IDownloadUrl {
  appId: IApp['id'];
  arch?: Arch | MacArch;
  artifactName?: LinuxArtifactName | MacArtifactName | WindowsArtifactName;
  buildId?: string;
  domain?: string;
  platform: 'linux' | 'mac' | 'windows' | 'universal';
}

export const getLinuxPgpMetadataUrl = (appId: IApp['id']) => {
  return `https://dl.todesktop.com/${appId}/apt/key-metadata.json`;
};

export const getDownloadUrl = ({
  appId,
  arch = 'x64',
  artifactName,
  buildId,
  domain,
  platform = 'universal',
}: IDownloadUrl) => {
  let rootUrl: string;

  if (!domain) {
    rootUrl = 'https://dl.todesktop.com';
  } else if (domain.startsWith('https://')) {
    rootUrl = domain;
  } else {
    rootUrl = 'https://' + domain;
  }

  let result = rootUrl.replace(/\/+$/, '');
  result += domain ? '' : `/${appId}`;

  // This is the download for a particular build
  if (buildId) {
    result += `/builds/${buildId}`;
  }

  if (platform !== 'universal') {
    result += `/${platform}`;

    if (artifactName) {
      result += `/${artifactName}/${arch}`;
    }
  }

  return result;
};

export const shouldShowDownloadUrl = ({
  wasArtifactCreated,
  artifactName,
  arch,
  platform,
  releasedBuild,
  app,
}: IDownloadMeta) => {
  if (!wasArtifactCreated) {
    return false;
  }

  if (app.appType !== 'electron' || platform === 'universal') {
    return true;
  }

  if (!releasedBuild) {
    return false;
  }

  return !!(releasedBuild as any)?.[platform]?.artifactDownloads?.[
    artifactName!
  ]?.[arch! as 'x64'];
};

export const takeScreenshot = async (
  url: string,
  height: number,
  width: number,
  deviceScaleFactor: number,
  appId: IApp['id'],
) => {
  const encryptedUrl = await encryptToBase64(url, appId);
  return `${awsLambdaBase}/screenshot/take-screenshot?screenshotId=${encodeURIComponent(
    encryptedUrl,
  )}&height=${height}&width=${width}&deviceScaleFactor=${deviceScaleFactor}&appId=${appId}`;
};
